// src/components/translation-modules/ImageManager.js
import React, { useState, useEffect } from "react";
import TranslationModuleBase from "./TranslationModuleBase";
import {
  FaTrash,
  FaUpload,
  FaCheckCircle,
  FaTimesCircle,
  FaImage,
  FaExclamationTriangle,
  FaSync,
} from "react-icons/fa";

// Use the correct domain WITHOUT www.
const API_BASE_URL = "https://salumidelnorcino.com";

const ImageManager = ({
  onSelectImage,
  selectedImage = null,
  directory = "img",
}) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Fetch images with better error handling
  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the direct API URL with a timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await fetch(
        `${API_BASE_URL}/api.php?action=listImages&directory=${directory}&_=${timestamp}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Origin: window.location.origin,
          },
          // Add cache control to avoid caching issues
          cache: "no-cache",
          // Add mode for better CORS handling
          mode: "cors",
          // Abort the request after 10 seconds
          signal: AbortSignal.timeout(10000),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch images: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(`API error: ${data.error}`);
      }

      setImages(data.images || []);
    } catch (err) {
      console.error("Error fetching images:", err);
      setError(`Failed to load images: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [directory]);

  // Handle file upload
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadingFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!uploadingFile) return;

    setUploadStatus({ loading: true, message: "Uploading..." });
    const formData = new FormData();
    formData.append("image", uploadingFile);
    formData.append("directory", directory);

    try {
      // Use the direct API URL
      const response = await fetch(`${API_BASE_URL}/api.php`, {
        method: "POST",
        body: formData,
        headers: {
          Origin: window.location.origin,
        },
        // Add cache control
        cache: "no-cache",
        // Add mode for better CORS handling
        mode: "cors",
        // Do not include credentials
        credentials: "omit",
        // Abort the request after 30 seconds
        signal: AbortSignal.timeout(30000),
      });

      if (!response.ok) {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.success) {
        setUploadStatus({
          success: true,
          message: "Image uploaded successfully!",
        });
        setUploadingFile(null);
        fetchImages();
      } else {
        setUploadStatus({
          error: true,
          message: data.error || "Failed to upload image.",
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadStatus({
        error: true,
        message: `Error uploading image: ${err.message}`,
      });
    }
  };

  const handleDeleteImage = async (fileName) => {
    if (!fileName) return;

    try {
      // Use the direct API URL
      const response = await fetch(`${API_BASE_URL}/api.php`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Origin: window.location.origin,
        },
        body: JSON.stringify({
          action: "deleteImage",
          directory: directory,
          fileName: fileName,
        }),
        // Add cache control
        cache: "no-cache",
        // Add mode for better CORS handling
        mode: "cors",
        // Do not include credentials
        credentials: "omit",
        // Abort the request after 10 seconds
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        throw new Error(
          `Delete failed: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.success) {
        fetchImages();
        setConfirmDelete(null);
      } else {
        setError(data.error || "Failed to delete image.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      setError(`Error deleting image: ${err.message}`);
    }
  };

  const confirmDeleteImage = (fileName) => {
    setConfirmDelete(fileName);
  };

  // Handle CORS troubleshooting message
  const renderCorsHelp = () => {
    if (!error || !error.includes("CORS")) return null;

    return (
      <div className="mt-2 p-3 bg-yellow-100 text-yellow-800 rounded-md">
        <h4 className="font-bold flex items-center">
          <FaExclamationTriangle className="mr-2" /> CORS Issue Detected
        </h4>
        <p className="text-sm mt-1">
          This may be caused by cross-origin restrictions. Make sure your API
          has these headers:
        </p>
        <pre className="bg-gray-100 p-2 rounded mt-1 text-xs overflow-auto">
          {`header("Access-Control-Allow-Origin: *");
header("Access-Control-Allow-Headers: Content-Type, Origin");
header("Access-Control-Allow-Methods: GET, POST, OPTIONS, DELETE");`}
        </pre>
        <p className="text-sm mt-2">
          Also check that your .htaccess is not redirecting non-www to www
          domains.
        </p>
      </div>
    );
  };

  return (
    <TranslationModuleBase title="Image Manager">
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
          {renderCorsHelp()}
          <button
            onClick={fetchImages}
            className="mt-2 px-3 py-1 bg-blue-500 text-white rounded flex items-center"
          >
            <FaSync className="mr-1" /> Try Again
          </button>
        </div>
      )}

      {/* Upload section */}
      <div className="mb-6 p-4 border border-dashed border-gray-300 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">Upload New Image</h3>
        <div className="flex items-center space-x-2">
          <input
            type="file"
            onChange={handleFileChange}
            accept="image/*"
            className="flex-grow"
          />
          <button
            onClick={handleUpload}
            disabled={!uploadingFile || (uploadStatus && uploadStatus.loading)}
            className={`px-4 py-2 rounded flex items-center ${
              !uploadingFile || (uploadStatus && uploadStatus.loading)
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-green-500 text-white hover:bg-green-600"
            }`}
          >
            <FaUpload className="mr-2" /> Upload
          </button>
        </div>

        {uploadStatus && (
          <div
            className={`mt-2 p-2 rounded flex items-center ${
              uploadStatus.error
                ? "bg-red-100 text-red-700"
                : uploadStatus.success
                ? "bg-green-100 text-green-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            {uploadStatus.error && <FaTimesCircle className="mr-2" />}
            {uploadStatus.success && <FaCheckCircle className="mr-2" />}
            {uploadStatus.loading && (
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {uploadStatus.message}
          </div>
        )}
      </div>

      {/* Images grid */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Available Images</h3>
          <button
            onClick={fetchImages}
            className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded flex items-center"
            title="Refresh images"
          >
            <FaSync className="mr-1" /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center p-4">
            <svg
              className="animate-spin h-8 w-8 mx-auto text-gray-500"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="mt-2 text-gray-600">Loading images...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center p-4 bg-gray-50 rounded">
            <FaImage className="mx-auto text-gray-400 text-4xl mb-2" />
            <p>No images found in /{directory}</p>
            <p className="text-sm text-gray-500 mt-1">
              Upload an image to get started
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {images.map((image) => (
              <div
                key={image.path}
                className={`border rounded-lg overflow-hidden hover:shadow-md transition-shadow ${
                  selectedImage === image.path ? "ring-2 ring-blue-500" : ""
                }`}
              >
                <div className="relative h-32 bg-gray-100">
                  <img
                    src={`${API_BASE_URL}${
                      image.path
                    }?_=${new Date().getTime()}`}
                    alt={image.name}
                    className="absolute inset-0 w-full h-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>';
                    }}
                  />
                </div>
                <div className="p-2">
                  <div className="text-sm truncate" title={image.name}>
                    {image.name}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <button
                      onClick={() => onSelectImage && onSelectImage(image.path)}
                      className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Select
                    </button>
                    <button
                      onClick={() => confirmDeleteImage(image.name)}
                      className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4 w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
            <p className="mb-4">
              Are you sure you want to delete the image{" "}
              <strong>{confirmDelete}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteImage(confirmDelete)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </TranslationModuleBase>
  );
};

export default ImageManager;
